import { useMemo, type Dispatch, type SetStateAction } from "react"
import { createPortal } from "react-dom"
import {
  ClipboardList,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Check,
  X,
  RotateCcw,
  ChevronsUpDown,
} from "lucide-react"
import type { ChecklistData } from "../lib/checklistTemplate"
import { useModalOpen } from "../lib/useModalOpen"
import { useGlassTokens } from "../lib/useAppBackground"
import { GlitchWord } from "./GlitchWord"

export interface ChecklistEditModalProps {
  checklist: ChecklistData
  setChecklist: Dispatch<SetStateAction<ChecklistData>>
  openCategories: Record<string, boolean>
  setOpenCategories: Dispatch<SetStateAction<Record<string, boolean>>>
  onClose: () => void
  onReset?: () => void
}

export function ChecklistEditModal({
  checklist,
  setChecklist,
  openCategories,
  setOpenCategories,
  onClose,
  onReset,
}: ChecklistEditModalProps) {
  const glass = useGlassTokens()
  useModalOpen(true)
  const categoryKeys = useMemo(() => Object.keys(checklist), [checklist])
  const defaultOpen = useMemo(() => {
    const o: Record<string, boolean> = {}
    categoryKeys.forEach((cat, i) => {
      o[cat] = i === 0
    })
    return o
  }, [categoryKeys])
  const isCategoryOpen = (cat: string) => openCategories[cat] ?? defaultOpen[cat] ?? false

  const allExpanded = categoryKeys.every((c) => isCategoryOpen(c))
  function toggleAllExpanded() {
    const next = !allExpanded
    setOpenCategories(categoryKeys.reduce((acc, c) => ({ ...acc, [c]: next }), {}))
  }

  const totalTasks = categoryKeys.reduce((n, c) => n + (checklist[c]?.tasks?.length ?? 0), 0)
  const checkedTasks = categoryKeys.reduce(
    (n, c) => n + (checklist[c]?.tasks?.filter((t) => t.completed).length ?? 0),
    0
  )

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 glass-backdrop"
        onClick={onClose}
        aria-hidden
      />
      <div className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col ${glass.cardBg} backdrop-blur-2xl rounded-2xl shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] border ${glass.border} overflow-hidden`}>
        {/* Header */}
        <div className={`shrink-0 px-4 py-3 border-b ${glass.borderLight}`}>
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <h3 className="text-[11px] font-mono font-bold text-white/90 uppercase tracking-widest flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-teal-400/90" />
                Protocol checklist
              </h3>
              <div className="font-mono text-[9px] flex items-center gap-1.5">
                <span className="text-white/15">0x03</span>
                <span className="text-white/10">&middot;</span>
                <span className="text-teal-400/60">
                  <GlitchWord word="protocol" intensity={1} colorBase="teal" />.edit()
                </span>
                {totalTasks > 0 && (
                  <>
                    <span className="text-white/10">&middot;</span>
                    <span className="text-white/25 tabular-nums">{checkedTasks}/{totalTasks}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleAllExpanded}
                className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                aria-label={allExpanded ? "Collapse all" : "Expand all"}
                title={allExpanded ? "Collapse all" : "Expand all"}
              >
                <ChevronsUpDown className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <div className={`rounded-2xl border ${glass.border} bg-white/[0.02] overflow-hidden`}>
            <div className={`p-3 border-b ${glass.borderLight}`}>
              <button
                type="button"
                onClick={() => {
                  const name = "New category"
                  setChecklist((prev) => ({ ...prev, [name]: { tasks: [] } }))
                  setOpenCategories((p) => ({ ...p, [name]: false }))
                }}
                className="flex items-center gap-2 text-[11px] font-mono text-white/40 hover:text-white/70 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add category
              </button>
            </div>
            {categoryKeys.map((category) => {
              const data = checklist[category]
              const tasks = data?.tasks ?? []
              const isOpen = isCategoryOpen(category)
              const catChecked = tasks.filter((t) => t.completed).length
              const allCatChecked = tasks.length > 0 && catChecked === tasks.length
              return (
                <div
                  key={category}
                  className={`border-b ${glass.borderLight} last:border-b-0`}
                >
                  <div className="w-full flex items-center gap-2 px-4 py-3 hover:bg-white/[0.04] transition-colors">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenCategories((p) => ({ ...p, [category]: !isOpen }))
                      }
                      className="p-0.5 rounded shrink-0 text-white/40 hover:text-white/70"
                    >
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    <input
                      type="text"
                      value={category}
                      onChange={(e) => {
                        const next = e.target.value
                        if (!next.trim()) return
                        setChecklist((prev) => {
                          const keys = Object.keys(prev)
                          const nextCl: ChecklistData = {}
                          keys.forEach((k) => {
                            nextCl[k === category ? next : k] = prev[k]
                          })
                          return nextCl
                        })
                        setOpenCategories((p) => {
                          const { [category]: val, ...rest } = p
                          return { ...rest, [next]: val }
                        })
                      }}
                      className="flex-1 min-w-0 text-[11px] font-mono font-semibold bg-transparent border-0 border-b border-transparent hover:border-white/20 focus:border-teal-400/50 focus:outline-none focus:ring-0 px-0 py-0.5 text-white/90 placeholder:text-white/40"
                    />
                    {tasks.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setChecklist((prev) => ({
                            ...prev,
                            [category]: {
                              tasks: prev[category].tasks.map((t) => ({
                                ...t,
                                completed: !allCatChecked,
                              })),
                            },
                          }))
                        }
                        className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                          allCatChecked
                            ? "bg-teal-500/30 border-teal-400/40 text-teal-400"
                            : "border-white/15 text-transparent hover:border-white/30 hover:text-white/30"
                        }`}
                        title={allCatChecked ? "Uncheck category" : "Check category"}
                      >
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setChecklist((prev) => {
                          const next = { ...prev }
                          delete next[category]
                          return next
                        })
                        setOpenCategories((p) => {
                          const { [category]: _, ...rest } = p
                          return rest
                        })
                      }}
                      className="p-1 rounded text-white/15 hover:text-red-400 hover:bg-red-500/10 shrink-0 transition-colors"
                      aria-label="Remove category"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-3 pt-0 pl-10 space-y-1.5">
                      {tasks.map((task, idx) => (
                        <div key={idx} className="flex items-center gap-2 group">
                          <button
                            type="button"
                            onClick={() =>
                              setChecklist((prev) => {
                                const cat = {
                                  ...prev[category],
                                  tasks: [...prev[category].tasks],
                                }
                                cat.tasks[idx] = {
                                  ...cat.tasks[idx],
                                  completed: !cat.tasks[idx].completed,
                                }
                                return { ...prev, [category]: cat }
                              })
                            }
                            className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              task.completed
                                ? "bg-emerald-500/80 border-emerald-400 text-white"
                                : "border-white/20 hover:border-white/40"
                            }`}
                            aria-label={task.completed ? "Uncheck" : "Check"}
                          >
                            {task.completed && (
                              <Check className="w-3 h-3" strokeWidth={3} />
                            )}
                          </button>
                          <input
                            type="text"
                            value={task.task}
                            onChange={(e) =>
                              setChecklist((prev) => {
                                const cat = {
                                  ...prev[category],
                                  tasks: [...prev[category].tasks],
                                }
                                cat.tasks[idx] = {
                                  ...cat.tasks[idx],
                                  task: e.target.value,
                                }
                                return { ...prev, [category]: cat }
                              })
                            }
                            className={`flex-1 min-w-0 px-3 py-1.5 rounded-lg border ${glass.border} bg-white/[0.04] text-[11px] font-mono text-white/80 placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-teal-400/30`}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setChecklist((prev) => {
                                const cat = {
                                  ...prev[category],
                                  tasks: prev[category].tasks.filter(
                                    (_, i) => i !== idx
                                  ),
                                }
                                return { ...prev, [category]: cat }
                              })
                            }
                            className="p-1.5 rounded text-red-400/80 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            aria-label="Remove task"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setChecklist((prev) => {
                            const cat = {
                              ...prev[category],
                              tasks: [
                                ...prev[category].tasks,
                                { task: "New task", completed: false },
                              ],
                            }
                            return { ...prev, [category]: cat }
                          })
                        }
                        className="flex items-center gap-1.5 text-[11px] font-mono text-white/40 hover:text-white/70 py-1 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add task
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer */}
        <div className={`shrink-0 flex items-center gap-3 px-4 py-3 border-t ${glass.borderLight}`}>
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              className="flex items-center gap-1.5 py-2 px-3 rounded-lg border border-amber-400/20 bg-amber-500/10 text-amber-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-amber-500/20 transition-colors mr-auto"
              title="Discard edits and restore the linked protocol"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset to protocol
            </button>
          )}
          <div className={onReset ? "" : "ml-auto"} />
          <button
            type="button"
            onClick={onClose}
            className="py-2.5 px-5 rounded-lg border border-teal-400/25 bg-teal-500/15 text-teal-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-teal-500/25 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
